// ============================================================================
// Shared Domain & API Types for Ramsay Maintenance Assessment Prep
// ============================================================================

export type TrackType = 'mechanical' | 'electrical' | 'multicraft' | 'aptitude';
export type ExtendedTrackType = TrackType | 'all';

export type ExamMode = 'study' | 'timed_mock';
export type ExamStatus = 'in_progress' | 'completed' | 'abandoned';
export type Difficulty = 'easy' | 'medium' | 'hard';
export type UserRole = 'user' | 'admin';
export type SubscriptionPlan = 'free' | 'pro_monthly' | 'lifetime_pass';
export type SubscriptionStatus = 'inactive' | 'active' | 'trialing' | 'past_due' | 'canceled';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  avatarUrl?: string | null;
  googleId?: string | null;
  authProvider?: string;
  emailVerified?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface Subscription {
  id: string;
  userId: string;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  provider?: 'stripe' | 'apple' | 'google' | null;
  appleOriginalTransactionId?: string | null;
  planType: SubscriptionPlan;
  status: SubscriptionStatus;
  currentPeriodEnd?: number | null;
  createdAt: number;
  updatedAt: number;
}

export function isProSubscriber(subscription?: Subscription | null): boolean {
  if (!subscription) return false;
  return (
    (subscription.planType === 'pro_monthly' || subscription.planType === 'lifetime_pass') &&
    (subscription.status === 'active' || subscription.status === 'trialing')
  );
}

/**
 * Ramsay Assessment Benchmark: ~60 seconds (1 minute) per question.
 * Full exam is 60 questions in 60 minutes (3600s).
 * Short practice sets scale dynamically (e.g. 5 questions = 5 mins, 10 questions = 10 mins).
 */
export function calculateExamTimeLimitSeconds(questionCount: number): number {
  if (questionCount <= 0) return 0;
  return Math.min(Math.max(questionCount * 60, 60), 3600);
}

export interface Category {
  id: string;
  track: TrackType;
  name: string;
  slug: string;
  description?: string | null;
  questionCount?: number;
  createdAt: number;
}

export interface Question {
  id: string;
  categoryId: string;
  categoryName?: string;
  track?: TrackType;
  prompt: string;
  options: string[]; // parsed from options_json
  correctIndex: number;
  explanation: string;
  imageUrl?: string | null;
  difficulty: Difficulty;
  createdAt: number;
}

export interface ExamSession {
  id: string;
  userId: string;
  track: ExtendedTrackType;
  mode: ExamMode;
  score: number;
  totalQuestions: number;
  timeLimitSeconds: number;
  status: ExamStatus;
  startedAt: number;
  completedAt?: number | null;
  answers?: SessionAnswer[];
}

export interface SessionAnswer {
  id: string;
  sessionId: string;
  questionId: string;
  selectedIndex: number | null;
  isCorrect: boolean;
  timeSpentMs: number;
  createdAt: number;
  question?: Question;
}

// ----------------------------------------------------------------------------
// API Payloads
// ----------------------------------------------------------------------------

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
}

export interface AuthResponse {
  token: string;
  user: User;
  subscription: Subscription;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface GoogleVerifyRequest {
  credential?: string;
  idToken?: string;
  accessToken?: string;
}

export interface GoogleConfigResponse {
  configured: boolean;
  clientId: string | null;
}

export interface StartExamRequest {
  track: ExtendedTrackType;
  mode: ExamMode;
  categoryId?: string;
  questionCount?: number;
  timeLimitSeconds?: number;
}

export interface SubmitAnswerRequest {
  questionId: string;
  selectedIndex: number;
  timeSpentMs: number;
}

export interface CompleteExamResponse {
  sessionId: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  passed: boolean;
  completedAt: number;
  answers: Array<{
    questionId: string;
    selectedIndex: number | null;
    correctIndex: number;
    isCorrect: boolean;
    explanation: string;
  }>;
}

export interface TrackSummary {
  track: TrackType;
  title: string;
  description: string;
  iconName: string;
  accentColor: string;
  totalQuestions: number;
  categories: Category[];
}
